/**
 * React Query hook for brand configuration
 * Provides cached, shared state for brand config across the Content App
 */

import { useQuery } from '@tanstack/react-query';
import { useHub } from '@/contexts/HubContext';
import { resolveBrandConfig } from '@/lib/design/brand-config';

// Query keys
export const brandConfigKeys = {
  all: ['brandConfig'] as const,
  detail: (operatorId: string, clientId?: string | null) => [...brandConfigKeys.all, operatorId, clientId] as const,
};

/**
 * Hook to fetch brand configuration for the current scope
 * Automatically uses selectedOperator, selectedClient from HubContext
 */
export function useBrandConfig() {
  const { selectedOperator, selectedClient } = useHub();

  return useQuery({
    queryKey: brandConfigKeys.detail(selectedOperator?.id ?? '', selectedClient?.id),
    queryFn: async () => {
      if (!selectedOperator) {
        return null;
      }

      return await resolveBrandConfig(selectedOperator, null, selectedClient ?? null);
    },
    enabled: !!selectedOperator,
    staleTime: 5 * 60 * 1000, // 5 min — Settings hooks invalidate this cache on save
  });
}
